import type { ScratchBlock, ScratchCategory, ScratchCodeBlock } from '@jeraisonne/ai';
import { SCRATCH_COLORS } from '@jeraisonne/ui';
import { Plus, Trash2 } from 'lucide-react';

const CATEGORIES: ScratchCategory[] = [
  'motion',
  'looks',
  'sound',
  'events',
  'control',
  'sensing',
  'operators',
  'variables',
  'my_blocks',
  'pen',
];

const INPUT =
  'rounded border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400';

let counter = 0;
function newId() {
  counter += 1;
  return `b${Date.now()}_${counter}`;
}

function newBlock(): ScratchBlock {
  return { id: newId(), category: 'motion', label: 'nouveau bloc', shape: 'stack' };
}

/** Édite une pile (liste de blocs) à un niveau donné. */
function ScratchListEditor({
  blocks,
  onChange,
}: {
  blocks: ScratchBlock[];
  onChange: (next: ScratchBlock[]) => void;
}) {
  const update = (i: number, next: ScratchBlock) =>
    onChange(blocks.map((b, j) => (j === i ? next : b)));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const add = () => onChange([...blocks, newBlock()]);

  return (
    <div className="space-y-2">
      {blocks.map((b, i) => (
        <BlockEditor
          key={b.id}
          block={b}
          onChange={(n) => update(i, n)}
          onRemove={() => remove(i)}
        />
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50"
      >
        <Plus className="h-3 w-3" /> bloc
      </button>
    </div>
  );
}

function BlockEditor({
  block,
  onChange,
  onRemove,
}: {
  block: ScratchBlock;
  onChange: (next: ScratchBlock) => void;
  onRemove: () => void;
}) {
  const isC = block.shape === 'c';
  const color = SCRATCH_COLORS[block.category];

  return (
    <div
      className="rounded-lg border-l-4 bg-white p-2 shadow-sm"
      style={{ borderLeftColor: color }}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <select
          value={block.category}
          onChange={(e) => onChange({ ...block, category: e.target.value as ScratchCategory })}
          className={INPUT}
          style={{ borderLeftColor: color }}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={block.shape ?? 'stack'}
          onChange={(e) => onChange({ ...block, shape: e.target.value as ScratchBlock['shape'] })}
          className={INPUT}
        >
          <option value="hat">chapeau</option>
          <option value="stack">empilable</option>
          <option value="c">C (boucle/si)</option>
          <option value="cap">terminale</option>
        </select>
        <input
          type="text"
          value={block.label}
          placeholder="texte (utilise {0}, {1}…)"
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          className={`${INPUT} min-w-[10rem] flex-1`}
        />
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-red-500 hover:bg-red-50"
          title="Supprimer"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <input
        type="text"
        value={(block.inputs ?? []).join(', ')}
        placeholder="valeurs des {n}, séparées par des virgules"
        onChange={(e) =>
          onChange({
            ...block,
            inputs: e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
              .map((s) => (s !== '' && !Number.isNaN(Number(s)) ? Number(s) : s)),
          })
        }
        className={`${INPUT} mt-1.5 w-full`}
      />
      {isC && (
        <div className="mt-2 space-y-2 border-l border-dashed border-slate-200 pl-3">
          <p className="text-[10px] font-semibold uppercase text-slate-400">corps</p>
          <ScratchListEditor
            blocks={block.children ?? []}
            onChange={(children) => onChange({ ...block, children })}
          />
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={block.elseChildren !== undefined}
              onChange={(e) =>
                onChange({ ...block, elseChildren: e.target.checked ? [] : undefined })
              }
              className="h-3.5 w-3.5 accent-indigo-600"
            />
            branche « sinon »
          </label>
          {block.elseChildren !== undefined && (
            <ScratchListEditor
              blocks={block.elseChildren}
              onChange={(elseChildren) => onChange({ ...block, elseChildren })}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function ScratchCodeForm({
  block,
  onChange,
}: {
  block: ScratchCodeBlock;
  onChange: (next: ScratchCodeBlock) => void;
}) {
  const updateScript = (i: number, next: ScratchBlock[]) =>
    onChange({ ...block, scripts: block.scripts.map((s, j) => (j === i ? next : s)) });

  return (
    <div className="space-y-3">
      {block.scripts.map((script, i) => (
        <div key={i} className="rounded-lg bg-slate-50 p-2">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase text-slate-400">Pile {i + 1}</p>
            {block.scripts.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  onChange({ ...block, scripts: block.scripts.filter((_, j) => j !== i) })
                }
                className="text-[11px] text-red-500 hover:underline"
              >
                supprimer la pile
              </button>
            )}
          </div>
          <ScratchListEditor blocks={script} onChange={(next) => updateScript(i, next)} />
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange({ ...block, scripts: [...block.scripts, [newBlock()]] })}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
      >
        <Plus className="h-3.5 w-3.5" /> Ajouter une pile
      </button>
    </div>
  );
}
